import React, { useMemo } from 'react';
import { Backdrop, Box, Fade, makeStyles, Modal } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import {
  APPLY_BUTTON_TEXT,
  SEARCH_FILTER_CLEAR_LABEL,
  SEARCH_FILTER_LABEL,
  SEARCH_FILTER_SECTION_ALT_TEXT,
} from '../../../assets/copy';
import { actionSetFilterOverlayVisible } from '../../../store/Filter/FilterAction';
import { useComponentWillUnmount } from '../../../utils/useComponentWillUnmount';
import Typography from '../../../components/overrides/Typography';
import CloseButton from '../../../components/core/CloseButton';
import {
  trackClickWithDescription,
  trackPageImpression,
} from '../../../utils/useOneTag';
import { Button, TextButton } from '../../../components/spoon/Button';
import { getFiltersCount } from '../../../utils/searchFilters';
import { FILTER_TYPES } from '../../../utils/constants/FilterTypes';

const useStyles = makeStyles((theme) => ({
  filterContainer: {
    padding: theme.spacing(0, 3),
    [theme.breakpoints.up('md')]: {
      maxHeight: `calc(100vh - (73px + 80px + ${theme.spacing(10)}))`,
      overflowY: 'auto',
    },
    [theme.breakpoints.down('sm')]: {
      paddingBottom: theme.spacing(17),
      overflowY: 'auto',
    },
  },
  filterHeader: {
    padding: theme.spacing(2, 3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.palette.secondary[60]}`,
    textAlign: 'center',
    '& h1': {
      width: '100%',
    },
  },
  filter: {
    position: 'fixed',
    overflow: 'auto',
    height: 'auto',
    width: '100%',
    backgroundColor: theme.palette.background.white,
    [theme.breakpoints.up('md')]: {
      borderRadius: theme.spacing(0.5),
      height: 'auto',
      left: '50%',
      overflow: 'hidden',
      position: 'absolute',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      maxWidth: theme.spacing(58),
    },
    [theme.breakpoints.up('xxl')]: {
      maxWidth: theme.spacing(82),
    },
    [theme.breakpoints.down('sm')]: {
      height: '100%',
    },
  },
  buttons: {
    backgroundColor: theme.palette.background.white,
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    borderTop: `1px solid ${theme.palette.secondary[60]}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 3),
    '-webkit-backface-visibility': 'hidden',
    [theme.breakpoints.up('md')]: {
      position: 'static',
      flexDirection: 'row',
    },
    gap: theme.spacing(2),
  },
  button: {
    width: '100%',
  },
  closeBtn: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(2),
  },
}));

const FilterModalContainer = ({
  children,
  onApply,
  onReset,
  selectedFilters,
  onClose = () => {},
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { filterOverlayVisible, currentFilters } = useSelector(
    (state) => state.filters
  );

  const calculateFiltersCount = (filters) =>
    getFiltersCount(filters, [
      FILTER_TYPES.VENUE_TYPE,
      FILTER_TYPES.CATEGORIES,
    ]);

  const filtersCount = useMemo(
    () => calculateFiltersCount(currentFilters),
    [currentFilters]
  );
  const selectedFiltersCount = useMemo(() => {
    if (!selectedFilters) return 0;
    return calculateFiltersCount(selectedFilters);
  }, [selectedFilters]);

  const handleHideModal = () => {
    dispatch(actionSetFilterOverlayVisible(false));
    onClose();
  };

  const handleClose = () => {
    trackClickWithDescription('search-revamp-filter-close', 'Button', '');
    handleHideModal();
  };

  const handleApply = () => {
    trackClickWithDescription('search-revamp-filter-apply', 'Button', '');
    onApply();
    handleHideModal();
  };

  const handleReset = () => {
    trackClickWithDescription('search-revamp-filter-reset', 'Button', '');
    onReset();
    handleHideModal();
  };

  const handleRender = () => {
    trackPageImpression('search-revamp', 'search-revamp-modal', '');
  };

  // Ensure modal hides when page changes
  useComponentWillUnmount(() => {
    if (filterOverlayVisible) handleHideModal();
  });

  return (
    <Modal
      aria-label={SEARCH_FILTER_SECTION_ALT_TEXT}
      open={filterOverlayVisible}
      onClose={handleClose}
      onRendered={handleRender}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}>
      <Fade in={filterOverlayVisible}>
        <Box className={classes.filter}>
          <Box aria-modal="true" aria-labelledby="title" role="dialog" className={classes.filterHeader}>
            <Box width='100%'>
              <Typography
                id="title" 
                component="h2"
                variant="medium1Semibold"
                width="100%">
                {SEARCH_FILTER_LABEL}
              </Typography>
            </Box>
            <CloseButton
              autoFocus
              fill="#121212"
              height={16}
              close={handleClose}
              width={16}
              className={classes.closeBtn}
            />
          </Box>
          <Box className={classes.filterContainer}>{children}</Box>
          <Box className={classes.buttons}>
            <Button onClick={handleApply} classes={{ root: classes.button }}>
              {APPLY_BUTTON_TEXT}
            </Button>
            <TextButton
              onClick={handleReset}
              disabled={filtersCount === 0 && selectedFiltersCount === 0}
              classes={{ root: classes.button }}>
              {SEARCH_FILTER_CLEAR_LABEL}
            </TextButton>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default FilterModalContainer;
